import { HiLightBulb } from "react-icons/hi";
import { cn } from "../../../../lib/utils";
import { themeColor, themeColorType } from "../../TemporaryProfile/TempProfile";
import { useEffect, useState } from "react";

export const GradientDivider = ({
  className,
  ...props
}: {
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
}) => {
  return (
    <div
      className={cn(
        "w-full h-0.5 bg-gradient-to-r from-gray-200/0 via-gray-200 to-gray-600/0 opacity-20",
        className
      )}
      {...props}
    ></div>
  );
};

export const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="w-full max-w-screen-2xl px-4 md:px-[8%] mx-auto">
      {children}
    </div>
  );
};

export const BadgeChips = ({
  badge,
  className,
  icon,
}: {
  badge: string;
  className?: string;
  icon?: React.ReactNode;
}) => {
  return (
    <div
      className={cn(
        "rounded-full bg-[#38434FFC] px-3 py-[6px] text-[12px] font-inter font-semibold flex gap-2",
        className
      )}
    >
      {icon ?? <HiLightBulb size={16} color="#F1F1F1" />}
      {badge}
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const randomColorsForContentChips = [
  "text-[#3771C8] bg-[rgba(55,113,200,0.12)]",
  "bg-[rgba(220,38,39,0.12)] text-[#DC2627]",
  "bg-[rgba(96,214,105,0.05)] text-[#60D669] ",
];

export const ContentChips = ({
  content,
  className,
}: {
  content: string;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        `flex justify-center items-center gap-2 px-3 py-1 text-[#3771C8] text-center text-xs not-italic font-normal leading-[normal] bg-[rgba(55,113,200,0.12)] rounded-[5px]`,
        className
      )}
    >
      {content}
    </div>
  );
};

export const GradientImage = ({
  image,
  altText,
  footer,
  width = 200,
  height = 200,
  className,
}: {
  image: string;
  altText: string;
  footer?: React.ReactNode;
  width?: number;
  height?: number;
  className?: string;
}) => {
  const [themeColorState, setThemeColorState] = useState({
    bgColorClass: "",
    gradientColorFromClass: "",
    IconBgClass: "",
  });
  useEffect(() => {
    const randomColor = (listOfColor: themeColorType) => {
      const index = Math.floor(Math.random() * listOfColor?.bg?.length);
      return [
        listOfColor?.bg[index],
        listOfColor?.from[index],
        listOfColor?.iconbg[index],
      ];
    };

    if (themeColor) {
      const indx = randomColor(themeColor);

      setThemeColorState({
        bgColorClass: indx[0],
        gradientColorFromClass: indx[1],
        IconBgClass: indx[2],
      });
    }
  }, []);

  return (
    <div
      className={cn(
        "relative w-[200px] h-[200px] overflow-hidden ",
        `w-[${width}px] h-[${height}px]`,
        className
      )}
    >
      <img
        src={image}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null; // prevents looping
          currentTarget.src =
            "https://collab.anchors.in/FeatredCreatorBackground.png";
        }}
        alt={`${altText} Pic`}
        className="z-[1] object-cover h-full w-full md:rounded-[8px]"
      />
      <div
        className={cn(
          `z-[2] absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-t from-[#171717] to-transparent`,
          `${themeColorState.gradientColorFromClass}`
        )}
      >
        <div className="flex flex-col justify-between w-full h-full items-center ">
          <div></div>
          {footer && footer}
        </div>
      </div>
    </div>
  );
};

interface FAQ {
  question: string;
  answer: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ChapterFaqs = (chapterName: string): FAQ[] => {
  return [
    {
      question:
        "What should I do if the city chapter shared with me isn’t the one for my location?",
      answer: `If the shared city chapter doesn’t match your location, don’t worry! You can still click on ""Join Chapter"" from the wrong city’s page to sign up and access your dashboard. Our system will automatically place you in the correct City Chapter based on your location. \n\nIf your city’s chapter isn’t available yet, hold tight—we’re launching chapters city by city wherever three or more LinkedIn creators are present. We’ll notify you as soon as yours goes live.`,
    },
    {
      question: `What does '${chapterName || "Gurgaon Chapter"}' mean?`,
      answer: `The '${chapterName}' is a dedicated community space for LinkedIn creators based in ${
        chapterName?.split(" ")?.[0]
      }. This local community allows LinkedIn creators from ${
        chapterName?.split(" ")?.[0]
      } to connect with each other, share ideas, and collaborate on projects. \n\nIt’s designed to support creators in the same city by giving them a common ground to connect with one another and grow together.`,
    },
    {
      question: "Am I eligible to join this chapter?",
      answer: `If you are a LinkedIn creator who currently lives in ${
        chapterName?.split(" ")?.[0]
      }, you are eligible to join the ${
        chapterName?.split(" ")?.[0]
      } Chapter. This chapter is open to creators of all follower counts and content types, as long as you are interested in connecting with other local creators and growing together within the ${
        chapterName?.split(" ")?.[0]
      } community.`,
    },
    {
      question: "Which chapter will be useful for me?",
      answer:
        "The chapter most useful to you is the one based on your current city of residence, as it will connect you with LinkedIn creators from your area. \n\nIf there’s no chapter for your exact location, joining a nearby City Chapter can also be beneficial, as it provides you with local support, collaboration, and insights relevant to your region. \n\nIf neither of these is possible yet, hold tight—we’re launching chapters city by city wherever three or more LinkedIn creators are present. We’ll notify you as soon as yours goes live.",
    },
    {
      question: "What will happen if I join a chapter?",
      answer:
        "When you join a chapter, you become part of a local network where other members from your city can connect with you. You’ll also be able to see other members from your city, enabling collaboration, mutual support, and growth. It’s a great way to connect with local creators who understand your challenges and can offer support and advice.",
    },
    {
      question: "Is there any fee to join this chapter?",
      answer:
        "No, joining a chapter is completely free. There is no membership fee. And, if you find value in the community there, you can also invite your LinkedIn creator-friends to join the chapter with you and reap the benefits of being a part of a local & strong community.",
    },
    {
      question:
        "Can I invite my fellow creators to join this chapter if they are from the same city?",
      answer:
        "Yes, you can invite other LinkedIn creators from your city to join the chapter by sharing the chapter’s unique link. Having more local creators in the chapter enhances the community experience, providing more opportunities for collaborations, shared learning, and growth.",
    },
    {
      question: "What are the benefits of joining a chapter?",
      answer:
        "Joining a chapter offers many benefits, including networking with local LinkedIn creators, finding collaboration opportunities, sharing insights and advice, and growing your local presence. \n\nThis community helps creators like you form a supportive network in your city, giving you a sense of belonging, making the journey to grow as a creator easier and more connected.",
    },
    {
      question: "Who are the other members in this chapter?",
      answer:
        "The other members in this chapter are fellow LinkedIn creators based in your city who, like you, want to connect, network and grow within the local community. \n\nThese members can range from newcomers to experienced influencers, each bringing unique perspectives and expertise to share within the circle.",
    },
    {
      question: "I have less than 5K followers. Can I still join?",
      answer:
        "Yes, you can join even if you have fewer than 5,000 followers. Chapters are basically city-based communities that are open to all LinkedIn creators, regardless of follower count. The focus is on networking, collaboration and support, so all creators—whether just starting or established—are welcome to participate and grow together.",
    },
    {
      question:
        "I have more than 100,000 followers. Will this chapter be useful for me too?",
      answer:
        "Absolutely. Even with a large following, joining a chapter can be valuable. It provides a chance to connect with local LinkedIn creators, explore new collaborations, and support the growth of emerging creators in your city. These communities can also offer you unique insights specific to your local audience, which gets lost at a wider level.",
    },
    {
      question:
        "I’m not originally from this city, but currently live here. Can I join?",
      answer:
        "Yes, you can definitely join! To be a part of our city chapter, you simply need to be a current resident of the city. This allows you to connect with like-minded individuals and build a supportive community for yourself, even if you're not originally from the area.",
    },
    {
      question: "Is there anything I can do besides joining a chapter?",
      answer: `Absolutely! anchors is dedicated to empowering content creators like you to grow and monetize your work.

Beyond joining a local chapter, you can leverage our platform to grow your following & engagement, connect with other content creators and learn from their experiences, partner with brands to earn income from your content, determine fair rates for brand sponsorships and other opportunities, and create a professional media kit to impress potential clients.

By utilizing these tools and resources, you can take your content creation journey to the next level.`,
    },
    {
      question: "How can I see who the other members are?",
      answer:
        "Only Chapter members can view the list of other members. Once you join, you’ll be able to see the full member list, including their LinkedIn profiles.",
    },
    {
      question: "Why did you build the chapter concept?",
      answer: `We understand that building a successful career as a content creator can be both exciting and isolating. Many creators have shared with us the challenges of feeling alone in their journey, lacking a local community of like-minded individuals to connect with. To address this, we developed the chapter concept.

By bringing together content creators within specific cities, we aim to encourage a sense of community and facilitate valuable connections. These local communities will enable you to grow your network, share resources, and collaborate on projects, & offer mutual support, ultimately accelerating your career as a content creator.`,
    },
    {
      question:
        "I love what you're building. Is there any way I can contribute?",
      answer:
        "Your support means a lot! Here are a few ways you can help: \n1) Vouch for us on LinkedIn to let others know about the benefits of joining a chapter; \n2) Share this with fellow creators who might find value in connecting with the local community; \n3) Post in your creator WhatsApp groups so other creators can benefit from joining as well. \n\nYour support is invaluable as we grow this community together!",
    },
    {
      question: "Can I switch to another chapter?",
      answer:
        "Yes, you can switch chapters if your location no longer matches your current one. Our system updates your chapter based on where you live, helping you connect with local creators in your city—whether you’ve just moved or have been there for years. Simply log in to access the community in your new location.",
    },
  ];
};
